// localStorage + date helpers for the route search (last search & recent
// airports). Pure functions, no React.

export const formatDateLocal = (date: Date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const LAST_SEARCH_STORAGE_KEY = "staffTraveler_lastSearch";

export interface LastSearchData {
  source: string;
  destination: string;
  dates: string[]; // Array of "YYYY-MM-DD" formatted dates
  savedAt: number; // Timestamp for potential cleanup
}

export const saveLastSearchToStorage = (
  source: string,
  destination: string,
  dates: string[],
): void => {
  try {
    const data: LastSearchData = { source, destination, dates, savedAt: Date.now() };
    localStorage.setItem(LAST_SEARCH_STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error("Error saving last search to localStorage:", error);
  }
};

export const loadLastSearchFromStorage = (): LastSearchData | null => {
  try {
    const stored = localStorage.getItem(LAST_SEARCH_STORAGE_KEY);
    if (!stored) return null;
    const data: LastSearchData = JSON.parse(stored);
    if (data.source && data.destination && Array.isArray(data.dates)) {
      return data;
    }
    return null;
  } catch (error) {
    console.error("Error loading last search from localStorage:", error);
    return null;
  }
};

// --- Last flight-number search (Search by Flight No.) ---
const LAST_FLIGHT_SEARCH_STORAGE_KEY = "staffTraveler_lastFlightSearch";

export interface LastFlightSearchData {
  flightNumber: string;
  dates: string[];
  savedAt: number;
}

export const saveLastFlightSearchToStorage = (
  flightNumber: string,
  dates: string[],
): void => {
  try {
    const data: LastFlightSearchData = { flightNumber, dates, savedAt: Date.now() };
    localStorage.setItem(LAST_FLIGHT_SEARCH_STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error("Error saving last flight search to localStorage:", error);
  }
};

export const loadLastFlightSearchFromStorage = (): LastFlightSearchData | null => {
  try {
    const stored = localStorage.getItem(LAST_FLIGHT_SEARCH_STORAGE_KEY);
    if (!stored) return null;
    const data: LastFlightSearchData = JSON.parse(stored);
    if (data.flightNumber && Array.isArray(data.dates)) {
      return data;
    }
    return null;
  } catch (error) {
    console.error("Error loading last flight search from localStorage:", error);
    return null;
  }
};

const RECENT_FROM_LOCATIONS_KEY = "staffTraveler_recentFromLocations";
const RECENT_TO_LOCATIONS_KEY = "staffTraveler_recentToLocations";

export interface RecentLocation {
  code: string;
  name: string;
  city?: string;
  country?: string;
  savedAt: number;
}

const recentKey = (type: "from" | "to") =>
  type === "from" ? RECENT_FROM_LOCATIONS_KEY : RECENT_TO_LOCATIONS_KEY;

export const saveRecentLocation = (
  type: "from" | "to",
  code: string,
  name: string,
  city?: string,
  country?: string,
): void => {
  try {
    const key = recentKey(type);
    const stored = localStorage.getItem(key);
    let recentLocations: RecentLocation[] = stored ? JSON.parse(stored) : [];
    // Drop any existing entry for this code, then add to the front. Keep 5.
    recentLocations = recentLocations.filter((loc) => loc.code !== code);
    recentLocations.unshift({ code, name, city, country, savedAt: Date.now() });
    recentLocations = recentLocations.slice(0, 5);
    localStorage.setItem(key, JSON.stringify(recentLocations));
  } catch (error) {
    console.error(`Error saving recent ${type} location to localStorage:`, error);
  }
};

export const loadRecentLocations = (type: "from" | "to"): RecentLocation[] => {
  try {
    const stored = localStorage.getItem(recentKey(type));
    if (!stored) return [];
    const data: RecentLocation[] = JSON.parse(stored);
    return data.sort((a, b) => b.savedAt - a.savedAt).slice(0, 5);
  } catch (error) {
    console.error(`Error loading recent ${type} locations from localStorage:`, error);
    return [];
  }
};
